import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { FiDownload, FiLink, FiSearch, FiX } from "react-icons/fi";
import Layout from "../../../components/layouts/Layout";
import http, { downloadUrl } from "../../../config/BaseUrl";
import ThreeInOne from "./3_In_1/ThreeInOne";
import ThreeInOneFast from "./3_In_1_fast/ThreeInOneFast";
import BoomerangGallery from "./BoomerangGallery";
import PhotoGallery from "./PhotoGallery";
import "./style/fullscreenview.css";
import VideoGallery from "./VideoGallery";
import MediaDownloadPublicLink from "../components/Modal/MediaDownloadPublicLink";
import { filterGalleryItemsByDate } from "./galleryDateFilter";

const EMPTY_ITEMS = [];
const EMPTY_FILTERS = { dateFrom: '', dateTo: '', name: '', phone: '' };

const GALLERY_CATEGORIES = [
  { value: "event_photo", label: "Photo", downloadType: "photos" },
  { value: "event_video", label: "Video", downloadType: "videos" },
  { value: "event_gif", label: "3 in 1", downloadType: "gif" },
  { value: "event_burst_gif", label: "3 in 1 fast", downloadType: "burst_gif" },
  { value: "boomerang", label: "Boomerang", downloadType: "boomerangs" },
];

function View() {
  const { id } = useParams();
  const [eventStatus, setStatus] = useState("event_photo");
  const [gallery, setGallery] = useState({ key: null, items: [], loading: true, error: '' });
  const [metadata, setMetadata] = useState({ eventId: null, data: null });
  const [retry, setRetry] = useState(0);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [nameFilter, setNameFilter] = useState("");
  const [phoneFilter, setPhoneFilter] = useState("");
  const [appliedFilters, setAppliedFilters] = useState({ dateFrom: "", dateTo: "", name: "", phone: "" });
  const [dateError, setDateError] = useState("");
  const location = useLocation();
  const requestKey = JSON.stringify([String(id), eventStatus, appliedFilters, retry]);
  const current = gallery.key === requestKey;
  const loading = !current || gallery.loading;
  const eventGallery = current ? gallery.items : EMPTY_ITEMS;
  const galleryError = current ? gallery.error : '';
  const event = metadata.eventId === String(id) ? metadata.data : null;
  const activeCategory = GALLERY_CATEGORIES.find((category) => category.value === eventStatus);
  const filteredGallery = useMemo(
    () => filterGalleryItemsByDate(eventGallery, appliedFilters.dateFrom, appliedFilters.dateTo),
    [appliedFilters.dateFrom, appliedFilters.dateTo, eventGallery]
  );

  useEffect(() => {
    let active = true;
    const eventId = String(id);
    const fromRoute = location.state && String(location.state.id) === eventId ? location.state : null;
    setMetadata({ eventId, data: fromRoute });
    if (!fromRoute?.uuid) {
      http.get(`show-event/${encodeURIComponent(eventId)}`).then(({ data }) => {
        if (active && data?.event && String(data.event.id) === eventId) setMetadata({ eventId, data: data.event });
      }).catch(() => { /* No stale event name or download UUID is retained. */ });
    }
    return () => { active = false; };
  }, [id, location.state]);

  useEffect(() => {
    setDateFrom(''); setDateTo(''); setNameFilter(''); setPhoneFilter(''); setDateError('');
    setAppliedFilters(previous => Object.values(previous).some(Boolean) ? EMPTY_FILTERS : previous);
  }, [id]);

  useEffect(() => {
    let active = true;
    const key = requestKey;
    setGallery({ key, items: [], loading: true, error: '' });
    const params = { event_id: id, gallery_item: eventStatus };
    if (appliedFilters.dateFrom) params.date_from = appliedFilters.dateFrom;
    if (appliedFilters.dateTo) params.date_to = appliedFilters.dateTo;
    if (appliedFilters.name) params.name = appliedFilters.name;
    if (appliedFilters.phone) params.phone = appliedFilters.phone;
    const expectedType = { event_photo: 'photo', event_video: 'video', event_gif: 'gif', event_burst_gif: 'burst_gif', boomerang: 'boomerang' }[eventStatus];
    http.get('gallery-items', { params }).then(({ data }) => {
      if (!active) return;
      if (!Array.isArray(data?.gallery_photo) || data.gallery_photo.some(item => String(item.event_id) !== String(id) || item.media_type !== expectedType)) {
        throw new Error('Unexpected gallery scope');
      }
      setGallery({ key, items: data.gallery_photo, loading: false, error: '' });
    }).catch(() => {
      if (active) setGallery({ key, items: [], loading: false, error: 'Could not load this event’s gallery. Please try again.' });
    });
    return () => { active = false; };
  }, [appliedFilters, eventStatus, id, requestKey]);

  const downloadAllData = (type) => {
    if (!event?.uuid) return;
    const params = new URLSearchParams({ type, event_id: event?.uuid || "" });
    if (appliedFilters.dateFrom) params.set("date_from", appliedFilters.dateFrom);
    if (appliedFilters.dateTo) params.set("date_to", appliedFilters.dateTo);
    if (appliedFilters.name) params.set("name", appliedFilters.name);
    if (appliedFilters.phone) params.set("phone", appliedFilters.phone);
    window.open(`${downloadUrl}file/download?${params.toString()}`);
  };

  const applyGalleryFilters = (event) => {
    event?.preventDefault();

    if (dateFrom && dateTo && dateFrom > dateTo) {
      setDateError("From date cannot be after To date.");
      return;
    }

    setDateError("");
    setAppliedFilters({
      dateFrom,
      dateTo,
      name: nameFilter.trim(),
      phone: phoneFilter.trim(),
    });
  };

  const clearGalleryFilters = () => {
    setDateFrom("");
    setDateTo("");
    setNameFilter("");
    setPhoneFilter("");
    setDateError("");
    setAppliedFilters({ dateFrom: "", dateTo: "", name: "", phone: "" });
  };

  const hasGalleryFilters = Boolean(
    dateFrom || dateTo || nameFilter || phoneFilter ||
    appliedFilters.dateFrom || appliedFilters.dateTo || appliedFilters.name || appliedFilters.phone
  );

  return (
    <Layout>
      <div className="card radius-10 event-gallery-page">
        <div className="card-body">
          <header className="event-gallery-header">
            <div>
              <span className="event-gallery-eyebrow">Online Gallery</span>
              <h1>{event?.event_name || "Event Gallery"}</h1>
              <p>Browse, filter, share and download media captured at this event.</p>
            </div>
            <span className="event-gallery-count">{filteredGallery.length} {filteredGallery.length === 1 ? "item" : "items"}</span>
          </header>

          <section className="event-gallery-toolbar" aria-label="Gallery controls">
            <div className="event-gallery-toolbar-main">
              <div className="event-gallery-tabs" role="tablist" aria-label="Gallery categories">
                {GALLERY_CATEGORIES.map((category) => (
                  <button
                    key={category.value}
                    type="button"
                    role="tab"
                    aria-selected={eventStatus === category.value}
                    className={eventStatus === category.value ? "active" : ""}
                    onClick={() => setStatus(category.value)}
                  >
                    {category.label}
                  </button>
                ))}
              </div>

              <div className="event-gallery-actions">
                <button type="button" disabled={!event?.uuid} onClick={() => downloadAllData(activeCategory?.downloadType || "all")}>
                  <FiDownload /> Download {activeCategory?.label || "Category"}
                </button>
                <button type="button" disabled={!event?.uuid} onClick={() => downloadAllData("all")}>
                  <FiDownload /> Download All
                </button>
                <button type="button" disabled={!event?.uuid} data-toggle="modal" data-target="#modal-event-gallery-download-link">
                  <FiLink /> Gallery Link
                </button>
              </div>
            </div>

            <form className="event-gallery-date-filter" onSubmit={applyGalleryFilters}>
              <div className="event-gallery-date-filter-title"><FiSearch /><span>Filter gallery</span></div>
              <label><span>Name</span><input type="search" value={nameFilter} placeholder="Guest name" onChange={(e) => setNameFilter(e.target.value)} /></label>
              <label><span>Phone</span><input type="tel" value={phoneFilter} placeholder="Phone number" onChange={(e) => setPhoneFilter(e.target.value)} /></label>
              <label><span>From</span><input type="date" value={dateFrom} max={dateTo || undefined} onChange={(e) => setDateFrom(e.target.value)} /></label>
              <label><span>To</span><input type="date" value={dateTo} min={dateFrom || undefined} onChange={(e) => setDateTo(e.target.value)} /></label>
              <button type="submit" className="event-gallery-apply-filter">Apply</button>
              {hasGalleryFilters ? (
                <button type="button" className="event-gallery-clear-filter" onClick={clearGalleryFilters}><FiX /> Clear</button>
              ) : null}
              {dateError ? <span className="event-gallery-date-error" role="alert">{dateError}</span> : null}
            </form>
          </section>

          {galleryError && <div role="alert" className="alert alert-danger">{galleryError} <button type="button" onClick={() => setRetry(value => value + 1)}>Retry gallery</button></div>}
          <div className="tab-content" id="pills-tabContent">
            <div
              className={`tab-pane fade ${
                eventStatus === "event_photo" ? `show active` : ""
              }`}
              id="pills-home"
              role="tabpanel"
              aria-labelledby="pills-home-tab"
            >
              {loading && loading ? (
                <div className="my-5 text-center col-md-12">
                  <span
                    className="spinner-border"
                    role="status"
                    aria-hidden="true"
                  ></span>
                </div>
              ) : (
                <PhotoGallery photo={filteredGallery} />
              )}
            </div>

            <div
              className={`tab-pane fade ${
                eventStatus === "event_video" ? `show active` : ""
              }`}
              id="pills-profile"
              role="tabpanel"
              aria-labelledby="pills-profile-tab"
            >
              {loading && loading ? (
                <div className="my-5 text-center col-md-12">
                  <span
                    className="spinner-border"
                    role="status"
                    aria-hidden="true"
                  ></span>
                </div>
              ) : (
                <VideoGallery video={filteredGallery} />
              )}
            </div>

            {/* <div
              className={`tab-pane fade ${eventStatus == 'event_gif' ? `show active` : ''}`}
              id="pills-contact"
              role="tabpanel"
              aria-labelledby="pills-contact-tab"
            >
              {loading && loading ?
                <div className="my-5 text-center col-md-12">
                  <span className="spinner-border" role="status" aria-hidden="true"></span>
                </div>
                :
                <RecentGifs gif={eventGallery} />
              }

            </div> */}

            <div
              className={`tab-pane fade ${
                eventStatus === "boomerang" ? `show active` : ""
              }`}
              id="pills-contact"
              role="tabpanel"
              aria-labelledby="pills-contact-tab"
            >
              {loading && loading ? (
                <div className="my-5 text-center col-md-12">
                  <span
                    className="spinner-border"
                    role="status"
                    aria-hidden="true"
                  ></span>
                </div>
              ) : (
                <BoomerangGallery boomerang={filteredGallery} />
              )}
            </div>

            <div
              className={`tab-pane fade ${
                eventStatus === "event_gif" ? `show active` : ""
              }`}
              id="pills-contact"
              role="tabpanel"
              aria-labelledby="pills-contact-tab"
            >
              {loading && loading ? (
                <div className="my-5 text-center col-md-12">
                  <span
                    className="spinner-border"
                    role="status"
                    aria-hidden="true"
                  ></span>
                </div>
              ) : (
                <ThreeInOne event_gif={filteredGallery} />
              )}
            </div>

            <div
              className={`tab-pane fade ${
                eventStatus === "event_burst_gif" ? `show active` : ""
              }`}
              id="pills-contact"
              role="tabpanel"
              aria-labelledby="pills-contact-tab"
            >
              {loading && loading ? (
                <div className="my-5 text-center col-md-12">
                  <span
                    className="spinner-border"
                    role="status"
                    aria-hidden="true"
                  ></span>
                </div>
              ) : (
                <ThreeInOneFast event_burst_gif={filteredGallery} />
              )}
            </div>
          </div>
        </div>
      </div>
      <MediaDownloadPublicLink 
				modalId={"modal-event-gallery-download-link"}
				selectedUuid={event?.uuid}
			/>
    </Layout>
  );
}

export default View;
